import { z } from 'zod';
/**
 * 手がかりカテゴリ
 */
export const CLUE_CATEGORIES = {
    person: { id: 'person', name: '人物', icon: '👤' },
    location: { id: 'location', name: '場所', icon: '📍' },
    item: { id: 'item', name: 'アイテム', icon: '🔑' },
    information: { id: 'information', name: '情報', icon: '📝' },
    event: { id: 'event', name: '出来事', icon: '⚡' },
    mystery: { id: 'mystery', name: '謎', icon: '❓' },
};
/**
 * 手がかり定義スキーマ
 */
export const ClueDefinitionSchema = z.object({
    /** 手がかりの一意ID */
    id: z.string(),
    /** 表示タイトル */
    title: z.string(),
    /** 詳細説明 */
    description: z.string(),
    /** カテゴリ */
    category: z.enum(['person', 'location', 'item', 'information', 'event', 'mystery']).default('information'),
    /** この手がかりを解放する変数名（trueになると解放） */
    unlockVariable: z.string(),
    /** 発見場所（表示用、オプション） */
    discoveredAt: z.string().optional(),
    /** 関連する手がかりID（オプション） */
    relatedClueIds: z.array(z.string()).optional(),
    /** 画像URL（オプション） */
    imageUrl: z.string().optional(),
    /** 重要度（1=低, 2=中, 3=高） */
    importance: z.number().min(1).max(3).default(2),
    /** ネタバレ警告（真相に近い情報） */
    isSpoiler: z.boolean().default(false),
});
/**
 * シナリオ内の手がかり定義配列スキーマ
 */
export const CluesConfigSchema = z.array(ClueDefinitionSchema);
/**
 * 初期手がかり状態を生成
 */
export function createInitialClueState(clueId) {
    return {
        id: clueId,
        unlocked: false,
        read: false,
    };
}
/**
 * 手がかりを解放
 */
export function unlockClue(state, clueId) {
    const existing = state.clues[clueId];
    if (existing?.unlocked) {
        return state; // 既に解放済み
    }
    return {
        ...state,
        clues: {
            ...state.clues,
            [clueId]: {
                ...(existing ?? createInitialClueState(clueId)),
                unlocked: true,
                unlockedAt: new Date().toISOString(),
            },
        },
    };
}
/**
 * 手がかりを既読にする
 */
export function markClueAsRead(state, clueId) {
    const existing = state.clues[clueId];
    if (!existing || existing.read) {
        return state;
    }
    return {
        ...state,
        clues: {
            ...state.clues,
            [clueId]: {
                ...existing,
                read: true,
            },
        },
    };
}
/**
 * 解放済み手がかり数を取得
 */
export function getUnlockedClueCount(state) {
    return Object.values(state.clues).filter((c) => c.unlocked).length;
}
/**
 * 未読手がかり数を取得
 */
export function getUnreadClueCount(state) {
    return Object.values(state.clues).filter((c) => c.unlocked && !c.read).length;
}
/**
 * カテゴリ別に手がかりをグループ化
 */
export function groupCluesByCategory(definitions, state) {
    const result = {
        person: [],
        location: [],
        item: [],
        information: [],
        event: [],
        mystery: [],
    };
    for (const def of definitions) {
        const clueState = state.clues[def.id] ?? createInitialClueState(def.id);
        if (clueState.unlocked) {
            result[def.category].push({ definition: def, state: clueState });
        }
    }
    return result;
}
//# sourceMappingURL=clueSchema.js.map